import { Inject, Injectable } from '@angular/core';
import {
  AuthSettings,
  AuthStatus,
  AuthWithCodeReq,
  CXoneAuth,
} from '@nice-devone/auth-sdk';
import { AuthToken } from '@nice-devone/common-sdk';
import { Subject } from 'rxjs';
import { LoggerService } from '../../../core/services/logger.service';
import { WindowRefService } from '../../../core/services/window.service';
import { DatadogService } from 'src/app/shared/services/data-dog.service';
import { quickWebLogger } from '@quickweb/logger';

import { jwtDecode } from 'jwt-decode';
import { NiceAuthenticationResponseService } from '../../../shared/services/nice-authentication-response.service';
import { IAutheticationNiceResponse } from '../util/Nice-authentication-response';

@Injectable({
  providedIn: 'root',
})
export class NiceSdkAuthenticationService {
  private readonly hostName = 'https://cxone.niceincontact.com';
  private readonly clientId = '1e31f761-e0ed-4c58-b644-446e7b4a503c';
  private readonly redirectUri = `${window.location.protocol}//${window.location.host}/logged`;
  private readonly codeChallenge = 'S256';
  private cxoneAuth = CXoneAuth.instance;
  private authState?: string;
  private authToken?: string;
  private authStatusSubject = new Subject<any>();
  public authStatus$ = this.authStatusSubject.asObservable();

  constructor(
    @Inject(WindowRefService) private windowRef: WindowRefService,
    private logger: LoggerService,
    private datadogService: DatadogService,
    private niceAuthenticationResponseService: NiceAuthenticationResponseService,
  ) {
    this.subscribeToAuthStatus();
  }

  private sendNiceAuthenticationResponseToDataDog(
    niceAuhtenticationResponse: IAutheticationNiceResponse,
  ) {
    switch (niceAuhtenticationResponse.type) {
      case 'autenticate': {
        this.datadogService.sendDataWithTimeout(() => {
          quickWebLogger.logger.info(niceAuhtenticationResponse.message, {
            reason: niceAuhtenticationResponse.message,
            details: niceAuhtenticationResponse.details,
          });
        });
        break;
      }

      case 'failed': {
        this.datadogService.sendDataWithTimeout(() => {
          quickWebLogger.logger.error(niceAuhtenticationResponse.message, {
            reason: niceAuhtenticationResponse.message,
            details: niceAuhtenticationResponse.details,
          });
        });
        break;
      }
      case 'refresh-token': {
        this.datadogService.sendDataWithTimeout(() => {
          quickWebLogger.logger.warn(niceAuhtenticationResponse.message, {
            reason: niceAuhtenticationResponse.message,
            details: niceAuhtenticationResponse.details,
          });
        });
        break;
      }

      default: {
        this.datadogService.sendDataWithTimeout(() => {
          quickWebLogger.logger.warn(niceAuhtenticationResponse.message, {
            reason: niceAuhtenticationResponse.message,
            details: niceAuhtenticationResponse.details,
          });
        });
        break;
      }
    }
  }

  private decodeNiceAuthToken(authToken: AuthToken) {
    const decoded: any = jwtDecode(authToken.accessToken);

    const decodedFormated = {
      given_name: decoded.given_name,
      iss: decoded?.iss || null,
      aud: decoded?.aud || null,
      tenant: decoded?.tenant || null,
      tenantId: decoded?.tenantId || null,
      role: decoded?.role || null,
      icBUId: decoded?.icBUId || null,
      icClusterId: decoded?.icClusterId || null,
    };

    const tokenDetails = {
      ...decodedFormated,
      expire_time: authToken.expiresIn,
      access_token_time: new Date(authToken.accessTokenTime),
    };

    return tokenDetails;
  }

  private subscribeToAuthStatus() {
    this.cxoneAuth.onAuthStatusChange.subscribe((data) => {
      this.logger.info(
        'SDK NICE (status) - status recebido:',
        JSON.stringify(data.status),
      );
      this.authStatusSubject.next(data.status);
      this.authState = this.mapAuthStatus(data.status);

      if (data.status === AuthStatus.AUTHENTICATED) {
        this.logger.info('SDK NICE (status) - Usuario autenticado.');

        this.authToken = (data.response as AuthToken).accessToken;
        this.cxoneAuth.startRefreshTokenCheck(
          this.cxoneAuth.getAuthToken(),
          true,
          this.cxoneAuth.getAuthState().isTokenValid,
        );

        const niceAutenticationResponse: IAutheticationNiceResponse = {
          type: 'autenticate',
          message: 'Usuário autenticado pela primeira vez',
          details: data,
        };

        const niceAutenticationResponseInStringify =
          this.niceAuthenticationResponseService.toStringify(
            niceAutenticationResponse,
          );
        window.localStorage.setItem(
          this.niceAuthenticationResponseService.localStorageKey,
          niceAutenticationResponseInStringify,
        );
      } else if (data.status === AuthStatus.AUTHENTICATION_FAILED) {
        this.logger.warn(
          'SDK NICE (status) - Falha na autenticacao, token expirado',
        );
        const niceAutenticationResponse: IAutheticationNiceResponse = {
          type: 'failed',
          message: 'Usuário não conseguiu se autenticar',
          details: data,
        };

        const niceAutenticationResponseInStringify =
          this.niceAuthenticationResponseService.toStringify(
            niceAutenticationResponse,
          );

        window.localStorage.setItem(
          this.niceAuthenticationResponseService.localStorageKey,
          niceAutenticationResponseInStringify,
        );
      } else if (data.status === AuthStatus.NOT_AUTHENTICATED) {
        this.logger.info('SDK NICE (status) - Usuario nao autenticado.');
        window.localStorage.setItem('authItem', 'Usuario não autenticado.');
      } else if (data.status === AuthStatus.REFRESH_TOKEN_SUCCESS) {
        this.logger.info('SDK NICE (status) - Token atualizado com sucesso.');

        const niceAutenticationResponse: IAutheticationNiceResponse = {
          type: 'refresh-token',
          message: 'Usuário realizou um refresh token',
          details: data,
        };

        const niceAutenticationResponseInStringify =
          this.niceAuthenticationResponseService.toStringify(
            niceAutenticationResponse,
          );

        window.localStorage.setItem(
          this.niceAuthenticationResponseService.localStorageKey,
          niceAutenticationResponseInStringify,
        );
      } else {
        this.logger.info(
          'SDK NICE (status) - Status de autenticacao nao tratado:',
          data.status,
        );

        const niceAutenticationResponse: IAutheticationNiceResponse = {
          type: 'unknow',
          message: 'Situação não mapeada',
          details: data,
        };

        const niceAutenticationResponseInStringify =
          this.niceAuthenticationResponseService.toStringify(
            niceAutenticationResponse,
          );

        window.localStorage.setItem(
          this.niceAuthenticationResponseService.localStorageKey,
          niceAutenticationResponseInStringify,
        );
      }
    });
  }

  //Verifica se o usuário está autenticado utilizando
  async isAuthenticated(isResolverInitialization = false): Promise<boolean> {
    //this.logger.info('SDK NICE (isAuthenticated) - Verifica se o usuário está autenticado');
    try {
      const authToken = this.cxoneAuth.getAuthToken();
      const isTokenValid = this.cxoneAuth.getAuthState().isTokenValid;
      // this.logger.info(`SDK NICE (isAuthenticated) - Token Valido: ${isTokenValid}`);

      const niceAuhtenticationResponseStrigify = localStorage.getItem(
        this.niceAuthenticationResponseService.localStorageKey,
      );

      if (
        isResolverInitialization === false &&
        niceAuhtenticationResponseStrigify !== null
      ) {
        const niceAuhtenticationResponse =
          this.niceAuthenticationResponseService.toJson(
            niceAuhtenticationResponseStrigify,
          );
        this.sendNiceAuthenticationResponseToDataDog(
          niceAuhtenticationResponse,
        );
        localStorage.removeItem(
          this.niceAuthenticationResponseService.localStorageKey,
        );
      }

      if (
        authToken.accessToken === undefined ||
        authToken.accessToken === null
      ) {
        return false;
      }

      const dataDogTokenDetails = this.decodeNiceAuthToken(authToken);

      if (isTokenValid === false && isResolverInitialization === false) {
        this.datadogService.sendDataWithTimeout(() => {
          quickWebLogger.logger.error(
            'Autenticação com falha: token inválido',
            {
              details: dataDogTokenDetails,
              reason: 'Autenticação com falha: token inválido',
            },
          );
        });
      }

      if (isTokenValid && isResolverInitialization === false) {
        this.datadogService.sendDataWithTimeout(() => {
          quickWebLogger.logger.info('token : token chegou valido', {
            details: dataDogTokenDetails,
            reason: 'token : token chegou valido',
          });
        });
      }

      return authToken && isTokenValid;
    } catch (error) {
      this.logger.error(
        'SDK NICE (isAuthenticated) - Erro ao verificar autenticação:',
        error,
      );

      if (isResolverInitialization === false) {
        this.datadogService.sendDataWithTimeout(() => {
          quickWebLogger.logger.error(
            ' SDK NICE (isAuthenticated) - Erro ao verificar autenticação',
            {
              error: error,
            },
          );
        });
      }

      return false;
    }
  }

  //Inicia o processo de autenticação, restaura dados de autenticação, verifica se há um código de autenticação
  //na url e lida de forma apropriada com o tipo de autenticação (popup ou página)
  async initialize(): Promise<void> {
    //this.logger.info('SDK NICE (initialize) - inicializando');

    // Retorna uma Promise que será resolvida quando o status for AUTHENTICATED
    return new Promise<void>((resolve, reject) => {
      //this.subscribeToAuth(resolve, reject); // Passa os callbacks de resolução/rejeição
      this.cxoneAuth.restoreData();

      //this.logger.info(this.cxoneAuth)
      const code = this.getQueryParam('code');

      if (!code) {
        this.logger.warn(
          'SDK NICE (initialize) - Nenhum código de autenticação encontrado na URL',
        );

        reject(new Error('Nenhum código de autenticação encontrado na URL'));
        return;
      }

      const displayMode = localStorage.getItem('display_mode');
      if (displayMode === 'popup') {
        this.handlePopupAuthentication(code);
      } else {
        const authSetting = this.getAuthSettingsFromStorage();
        if (authSetting) {
          this.cxoneAuth.init(authSetting);
          this.fetchAccessToken(code).catch((error) => {
            this.logger.error(
              'SDK NICE (initialize) - Erro ao buscar token de acesso:',
              error,
              code,
            );
            reject(error);
          });
        } else {
          this.datadogService.sendDataWithTimeout(() => {
            quickWebLogger.logger.error(
              'SDK NICE (initialize) - Configurações de autenticação não encontradas no armazenamento local',
            );
          });
          this.logger.error(
            'SDK NICE (initialize) - Configurações de autenticação não encontradas no armazenamento local',
          );
          reject(
            new Error(
              'Configurações de autenticação não encontradas no armazenamento local',
            ),
          );
        }
      }
      //this.logger.info('SDK NICE (initialize) - fim do inicializador');
    });
  }

  //Obtém o valor de um parâmetro da URL
  private getQueryParam(param: string): string | null {
    //this.logger.info(this.windowRef.nativeWindow.location)
    const searchParams = new URLSearchParams(
      this.windowRef.nativeWindow.location.search,
    );
    // this.logger.info(this.windowRef.nativeWindow.location.search)
    return searchParams.get(param);
  }

  //Recupera as configurações de autenticação do armazenamento local
  private getAuthSettingsFromStorage(): AuthSettings | null {
    const authConsumer = localStorage.getItem('auth_consumer');
    return authConsumer ? JSON.parse(authConsumer) : null;
  }

  //Lida com a autenticação via popup, enviando o código de autenticação de volta para a janela pai
  private handlePopupAuthentication(code: string): void {
    const message = { messageType: 'Authenticated', code };
    this.windowRef.nativeWindow.opener?.postMessage({ message }, '*');
  }

  //Valida as configurações de autenticação antes de inicializar o SDK
  private validateAuthInitSettings(): void {
    if (
      !this.hostName ||
      !this.clientId ||
      !this.redirectUri ||
      !this.codeChallenge
    ) {
      this.logger.error('SDK NICE - Configurações de autenticação inválidas');
      //throw new Error('Configurações de autenticação inválidas');
    }
  }

  //Inicializa as configurações de autenticação do SDK
  private initAuth() {
    //this.logger.info('SDK NICE (initAuth) - Inicializando configurações de autenticação');
    this.validateAuthInitSettings();
    const authSetting: AuthSettings = {
      cxoneHostname: this.hostName,
      clientId: this.clientId,
      redirectUri: this.redirectUri,
    };
    localStorage.setItem('auth_consumer', JSON.stringify(authSetting));
    this.cxoneAuth.init(authSetting);
  }

  //Obtém o token de acesso usando o código de autenticação
  private async fetchAccessToken(code: string): Promise<void> {
    const authObject: AuthWithCodeReq = { clientId: this.clientId, code };
    await this.cxoneAuth.getAccessTokenByCode(authObject);
  }

  //Inicia o processo de verificação do token de atualização
  /*private subscribeToAuth(resolve: () => void, reject: (error: any) => void) {
    //this.logger.info('SDK NICE (subscribeToAuth) - inicio subscribe to auth');
    this.cxoneAuth.onAuthStatusChange.subscribe((data) => {
      this.authState = this.mapAuthStatus(data.status);

      // Atualiza o BehaviorSubject com o novo status
      //this.logger.info('SDK NICE (subscribeToAuth) status alterado para', data.status);

      if (data.status === AuthStatus.AUTHENTICATED) {
        this.authToken = (data.response as AuthToken).accessToken;
        this.cxoneAuth.startRefreshTokenCheck(
          this.cxoneAuth.getAuthToken(),
          true,
          this.cxoneAuth.getAuthState().isTokenValid,
        );
        resolve(); // Resolve a Promise quando o status for AUTHENTICATED
      } else if (data.status === AuthStatus.AUTHENTICATION_FAILED) {
        reject(new Error('Falha na autenticação')); // Rejeita a Promise em caso de falha
      } else {
        this.logger.warn(
          'SDK NICE (subscribeToAuth) - Status de autenticação não tratado:',
          data.status,
        );
      }
    });
    //this.logger.info('SDK NICE (subscribeToAuth) - End subscribe to auth');
  }*/

  //Mapeia o status de autenticação para uma string legível
  private mapAuthStatus(status: AuthStatus): string {
    const statusMap: Record<AuthStatus, string> = {
      [AuthStatus.AUTHENTICATING]: 'AUTHENTICATING',
      [AuthStatus.AUTHENTICATED]: 'AUTHENTICATED',
      [AuthStatus.NOT_AUTHENTICATED]: 'NOT_AUTHENTICATED',
      [AuthStatus.AUTHENTICATION_FAILED]: 'AUTHENTICATION_FAILED',
      [AuthStatus.REFRESH_TOKEN_SUCCESS]: 'REFRESH_TOKEN_SUCCESS',
    };
    return statusMap[status] || 'UNKNOWN';
  }

  //Inicia o processo de autenticação, configurações e redireciona o usuário para a página de autenticação
  async authenticate(): Promise<void> {
    //this.logger.info('SDK NICE (authenticate) - Iniciando autenticação...');
    const displayMode = localStorage.getItem('display_mode');
    const centralId = localStorage.getItem('centralId') || '';

    try {
      this.initAuth();
      const authUrl = await this.cxoneAuth.getAuthorizeUrl(
        displayMode || 'popup',
        this.codeChallenge,
      );
      if (displayMode === 'popup') {
        await this.openPopupAndAuthenticate(authUrl, centralId);
      } else if (displayMode === 'page') {
        this.windowRef.nativeWindow.location.href = authUrl;
      }
    } catch (error) {
      this.logger.error(
        'SDK NICE (authenticate) - Erro na autenticacao: ' + error,
      );

      this.datadogService.sendDataWithTimeout(() => {
        quickWebLogger.logger.error('Falha na autenticação Nice', {
          error: error,
        });
      });
    }
  }

  //Abre um popup para autenticação e escuta a mensagem de autenticação
  private async openPopupAndAuthenticate(
    authUrl: string,
    centralId: string,
  ): Promise<void> {
    const popupOptions =
      'width=500,height=500,scrollbars=yes,toolbar=no,left=50,top=50';
    const popupWindow = this.windowRef.nativeWindow.open(
      authUrl,
      'authWindow',
      popupOptions,
    );

    try {
      popupWindow?.localStorage.setItem('centralId', centralId);
    } catch (e) {
      this.logger.error(
        'SDK NICE (openPopupAndAuthenticate) - Erro ao definir localStorage no popup:',
        e,
      );
    }

    const messageHandler = async (event: any) => {
      //this.logger.info('SDK NICE (openPopupAndAuthenticate) - Mensagem recebida:', event);
      const message = event.data.message;

      if (message?.messageType === 'Authenticated') {
        //this.logger.info('SDK NICE (openPopupAndAuthenticate) - Autenticado com sucesso', message);

        popupWindow?.close();

        await this.fetchAccessToken(message.code);

        this.windowRef.nativeWindow.location.assign(`/centrais/${centralId}`);

        this.windowRef.nativeWindow.removeEventListener(
          'message',
          messageHandler,
        );
      }
    };

    this.windowRef.nativeWindow.addEventListener(
      'message',
      messageHandler,
      false,
    );
  }

  //Carrega o agente CXone
  async launchAgent(): Promise<void> {
    //this.logger.info('SDK NICE (launchAgent) - Lançando CXone Agent');
    this.cxoneAuth.launchCXoneAgent(
      'launchCXA',
      'https://cxagent.nicecxone.com?src=sdk',
      {
        width: '100%',
        border: 'none',
      },
    );
  }
}
